import { defineStore } from "pinia";
import _ from "lodash";
import {
    NotificationState,
    NotificationType,
    NotificationError
} from "@/shared/types";
import {
    Environment,
    getEnvironment
} from "@/shared/env";

class NotificationImpl {
    constructor (readonly message: string, readonly type: NotificationType, readonly id: number) {
    }
}

export const initialState: NotificationState = {
    notifications: [],
};

export const shortTimeoutMs = 5000;
export const mediumTimeoutMs = 10000;
export const longTimeoutMs = 30000;

export const useNotifierStore = defineStore("notifier", {
    state: (): NotificationState => (_.cloneDeep(initialState)),
    getters: {
        shortTimeout: _ => shortTimeoutMs,
        mediumTimeout: _ => mediumTimeoutMs,
        longTimeout: _ => longTimeoutMs,
    },
    actions: {
        clearAlerts () {
            this.notifications = [];
        },
        clear (index) {
            this.notifications.splice(index, 1);
        },
        trySetTimeout (id: number, ms: number) {
          if (ms > 0) {
            setTimeout(() => {
                const index = this.notifications.findIndex(x => x.id === id);
                if (index === -1) return;
                this.notifications.splice(index, 1);
            }, ms);
          }
        },
        setDanger (message, timeoutMilliseconds = 0) {
            if (!message || getEnvironment() === Environment.Demo) return;
            const id = Math.random();
            this.notifications.push(new NotificationImpl(message, NotificationType.Danger, id));
            this.trySetTimeout(id, timeoutMilliseconds);
        },
        setInfo (message, timeoutMilliseconds = 0) {
            if (!message) return;
            const id = Math.random();
            this.notifications.push(new NotificationImpl(message, NotificationType.Info, id));
            this.trySetTimeout(id, timeoutMilliseconds);
        },
        setSuccess (message, timeoutMilliseconds = 0) {
            if (!message) return;
            const id = Math.random();
            this.notifications.push(new NotificationImpl(message, NotificationType.Success, id));
            this.trySetTimeout(id, timeoutMilliseconds);
        },
        setWarning (message, timeoutMilliseconds = 0) {
            if (!message) return;
            const id = Math.random();
            this.notifications.push(new NotificationImpl(message, NotificationType.Warning, id));
            this.trySetTimeout(id, timeoutMilliseconds);
        },
        error (errorObj: NotificationError): Promise<any> {
            return new Promise<any>((resolve, reject) => {
                let error = errorObj.error;
                const timeout = errorObj.timeout;
                if (typeof error === "string") {
                    this.setDanger(error, timeout);

                    if (errorObj.rethrow) {
                        reject(error);
                    }

                    return;
                }

                const baseErrorMessage = "Error processing your request. Please try again later.";

                if (!error) {
                    this.setDanger(baseErrorMessage);

                    if (errorObj.rethrow) {
                        reject(baseErrorMessage);
                    }

                    return;
                }

                if (error.payload) {
                    error = error.payload;
                }

                if (error.response) {
                    error = error.response;
                }

                if (error.data) {
                    error = error.data;
                }

                let message = baseErrorMessage;
                if (error.errorMessage) {
                    message = error.errorMessage;
                } else if (error.value && error.value.errorMessage) {
                    message = error.value.errorMessage;
                } else if (error.message) {
                    message = error.message;
                } else if (Array.isArray(error[Object.keys(error)[0]])) {
                    message = "";
                    Object.keys(error).forEach(i => {
                        error[i].forEach((m: string) => {
                            message += m + "<br/>";
                        });
                    });
                    message = message.slice(0, -5);
                }

                this.setDanger(message, timeout);

                if (errorObj.rethrow) {
                    reject(message);
                }

                return;
            });
        }
    },
});

